import React, { useState, useCallback, useEffect } from 'react';

type TutorialStep = {
  id: string;
  title: string;
  description: string;
  highlight?: string; // CSS selector for element to highlight
  action?: string; // What the user should do
  shortcut?: string;
};

const tutorialSteps: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Atlas IDE',
    description: 'This interactive tutorial will guide you through the main features of the IDE. You can navigate using the arrow buttons or press Escape to exit at any time.',
    action: 'Click Next to continue',
  },
  {
    id: 'file-explorer',
    title: 'File Explorer',
    description: 'The sidebar on the left shows your project files. Click on any file to open it in the editor. Right-click for context menu options like rename, delete, or create new files.',
    highlight: '.file-explorer',
    action: 'Try clicking on a file to open it',
  },
  {
    id: 'editor',
    title: 'Code Editor',
    description: 'The center panel is your main code editor with syntax highlighting, auto-completion, and IntelliSense. It supports multiple tabs for editing multiple files.',
    highlight: '.editor-area',
    shortcut: 'Cmd+S to save',
  },
  {
    id: 'preview',
    title: 'Live Preview',
    description: 'The right panel shows a live preview of your application. Changes are automatically reflected when you save. You can resize panels by dragging the dividers.',
    highlight: '.preview-panel',
    action: 'Try resizing the preview panel',
  },
  {
    id: 'command-palette',
    title: 'Command Palette',
    description: 'The command palette is your gateway to all IDE features. Press Cmd+Shift+P (or Ctrl+Shift+P) to open it and search for any command.',
    shortcut: 'Cmd+Shift+P',
    action: 'Try opening the command palette',
  },
  {
    id: 'goto-file',
    title: 'Quick File Navigation',
    description: 'Quickly navigate to any file in your project by pressing Cmd+P. Just start typing the filename and select from the results.',
    shortcut: 'Cmd+P',
    action: 'Try the quick file navigation',
  },
  {
    id: 'search',
    title: 'Search & Replace',
    description: 'Use Cmd+F for in-file search and Cmd+Shift+F for project-wide search. Replace functionality is available with Cmd+H and Cmd+Shift+H.',
    shortcut: 'Cmd+F / Cmd+Shift+F',
  },
  {
    id: 'git',
    title: 'Git Integration',
    description: 'The Source Control panel in the sidebar shows your Git changes. Stage files, write commit messages, and push/pull directly from the IDE.',
    highlight: '.source-control',
    action: 'Check out the Source Control panel',
  },
  {
    id: 'terminal',
    title: 'Integrated Terminal',
    description: 'The bottom panel includes an integrated terminal. Switch between Console, Terminal, Problems, and Network tabs to view different outputs.',
    highlight: '.log-tabs',
    action: 'Try switching between output tabs',
  },
  {
    id: 'zen-mode',
    title: 'Zen Mode',
    description: 'For distraction-free coding, press F11 to enter Zen Mode. This hides all panels except the editor. Press F11 again to exit.',
    shortcut: 'F11',
    action: 'Try Zen Mode for focused coding',
  },
  {
    id: 'shortcuts',
    title: 'Keyboard Shortcuts',
    description: 'Atlas IDE has many keyboard shortcuts to boost your productivity. Press Cmd+K Cmd+S to view the complete shortcuts cheatsheet.',
    shortcut: 'Cmd+K Cmd+S',
    action: 'View the keyboard shortcuts',
  },
  {
    id: 'complete',
    title: 'Tutorial Complete!',
    description: 'You\'ve completed the basic tutorial. Explore the command palette (Cmd+Shift+P) to discover more features like themes, Vim mode, code formatting, and more. Happy coding!',
    action: 'Click Finish to close the tutorial',
  },
];

type TutorialModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const TutorialModal = React.memo(function TutorialModal({
  isOpen,
  onClose,
}: TutorialModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightedElement, setHighlightedElement] = useState<Element | null>(null);

  const step = tutorialSteps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === tutorialSteps.length - 1;
  const progress = ((currentStep + 1) / tutorialSteps.length) * 100;

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
        if (!isLastStep) {
          setCurrentStep(prev => prev + 1);
        } else {
          onClose();
        }
      } else if (e.key === 'ArrowLeft') {
        if (!isFirstStep) {
          setCurrentStep(prev => prev - 1);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isFirstStep, isLastStep, onClose]);

  // Highlight element
  useEffect(() => {
    if (!isOpen || !step.highlight) {
      setHighlightedElement(null);
      return;
    }

    const element = document.querySelector(step.highlight);
    if (element) {
      setHighlightedElement(element);
      element.classList.add('tutorial-highlight');
    }

    return () => {
      if (element) {
        element.classList.remove('tutorial-highlight');
      }
    };
  }, [isOpen, step, currentStep]);

  const handleNext = useCallback(() => {
    if (!isLastStep) {
      setCurrentStep(prev => prev + 1);
    } else {
      onClose();
    }
  }, [isLastStep, onClose]);

  const handlePrev = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  const handleClose = useCallback(() => {
    setCurrentStep(0);
    onClose();
  }, [onClose]);

  const handleStepClick = useCallback((index: number) => {
    setCurrentStep(index);
  }, []);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="tutorial-backdrop"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 999,
        }}
        onClick={handleClose}
      />

      {/* Tutorial Panel */}
      <div
        className="tutorial-modal"
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '90%',
          maxWidth: '600px',
          background: 'var(--bg)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          zIndex: 1000,
          overflow: 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Progress Bar */}
        <div style={{ height: '4px', background: 'var(--bg-secondary)' }}>
          <div
            style={{
              height: '100%',
              width: `${progress}%`,
              background: 'var(--accent)',
              transition: 'width 0.3s ease',
            }}
          />
        </div>

        {/* Step Indicators */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '6px',
          padding: '12px',
          borderBottom: '1px solid var(--border)',
        }}>
          {tutorialSteps.map((s, index) => (
            <button
              key={s.id}
              onClick={() => handleStepClick(index)}
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                border: 'none',
                background: index === currentStep ? 'var(--accent)' : index < currentStep ? 'var(--success)' : 'var(--bg-secondary)',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              title={s.title}
            />
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '12px',
          }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>{step.title}</h3>
            <span style={{ fontSize: '12px', color: 'var(--muted)' }}>
              {currentStep + 1} / {tutorialSteps.length}
            </span>
          </div>

          <p style={{
            margin: '0 0 16px 0',
            fontSize: '14px',
            lineHeight: 1.6,
            color: 'var(--text-secondary)',
          }}>
            {step.description}
          </p>

          {(step.shortcut || step.action) && (
            <div style={{
              display: 'flex',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap',
            }}>
              {step.shortcut && (
                <div style={{
                  padding: '6px 12px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}>
                  <span style={{ color: 'var(--muted)' }}>Shortcut: </span>
                  <kbd style={{
                    padding: '2px 6px',
                    background: 'var(--bg)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    fontFamily: 'monospace',
                  }}>
                    {step.shortcut}
                  </kbd>
                </div>
              )}
              {step.action && (
                <div style={{
                  padding: '6px 12px',
                  background: 'var(--accent)',
                  color: 'white',
                  borderRadius: '6px',
                  fontSize: '13px',
                }}>
                  {step.action}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12px 20px',
          borderTop: '1px solid var(--border)',
          background: 'var(--bg-secondary)',
        }}>
          <button
            className="pill-btn"
            onClick={handleClose}
            style={{ opacity: 0.7 }}
          >
            Skip Tutorial
          </button>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="pill-btn"
              onClick={handlePrev}
              disabled={isFirstStep}
              style={{ opacity: isFirstStep ? 0.5 : 1 }}
            >
              Previous
            </button>
            <button
              className="pill-btn"
              onClick={handleNext}
              style={{ background: 'var(--accent)', color: 'white' }}
            >
              {isLastStep ? 'Finish' : 'Next'}
            </button>
          </div>
        </div>
      </div>

      {/* Highlight Arrow - pointing to highlighted element */}
      {highlightedElement && (
        <div
          style={{
            position: 'fixed',
            top: highlightedElement.getBoundingClientRect().top + highlightedElement.getBoundingClientRect().height / 2,
            left: highlightedElement.getBoundingClientRect().left - 30,
            transform: 'translateY(-50%)',
            fontSize: '24px',
            color: 'var(--accent)',
            zIndex: 1001,
            animation: 'pulse 1s infinite',
          }}
        >

        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: translateY(-50%) translateX(0); }
          50% { opacity: 0.7; transform: translateY(-50%) translateX(-5px); }
        }
        .tutorial-highlight {
          outline: 3px solid var(--accent) !important;
          outline-offset: 4px !important;
          z-index: 1000 !important;
          position: relative !important;
        }
      `}</style>
    </>
  );
});

export default TutorialModal;
